import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Filter, Star, Heart, Play, ArrowLeft, X, Clock, Calendar, Trash2, Grid, List, RotateCcw, Info } from 'lucide-react';
import { getWatchProgress } from '../mock/movies';

// Modal de Detalles
const MovieDetailModal = ({ movie, isOpen, onClose, onPlayVideo, onToggleFavorite, isFavorite, progress }) => {
  const hasProgress = progress > 0;

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          transition={{ type: "spring", duration: 0.5 }}
          className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-gradient-to-b from-gray-900 to-black rounded-2xl shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Botón Cerrar */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-10 p-2 bg-black/50 hover:bg-black/70 rounded-full backdrop-blur-sm border border-white/20 text-white transition-all"
          >
            <X className="w-6 h-6" />
          </button>

          {/* Imagen de Fondo */}
          <div className="relative h-96">
            <img
              src={movie.image}
              alt={movie.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/50 to-transparent" />
          </div>

          {/* Contenido */}
          <div className="p-8 -mt-32 relative z-10">
            <h2 className="text-4xl md:text-5xl font-black text-white mb-4">
              {movie.title}
            </h2>

            {/* Progress Indicator */}
            {hasProgress && (
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-2">
                  <RotateCcw className="w-4 h-4 text-green-400" />
                  <span className="text-green-400 font-semibold text-sm">Continuar viendo</span>
                  <span className="text-gray-400 text-sm">{progress}% completado</span>
                </div>
                <div className="w-full bg-gray-700/50 rounded-full h-2">
                  <div 
                    className="bg-green-500 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            )}

            {/* Movie Info */}
            <div className="flex flex-wrap items-center gap-4 mb-6">
              <div className="flex items-center gap-2 bg-yellow-500/20 px-3 py-1 rounded-full">
                <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                <span className="font-bold text-yellow-400">{movie.rating}/10</span>
              </div>
              {movie.year && (
                <div className="flex items-center gap-2 bg-blue-500/20 px-3 py-1 rounded-full">
                  <Calendar className="w-4 h-4 text-blue-400" />
                  <span className="font-medium text-blue-400">{movie.year}</span>
                </div>
              )}
              {movie.duration && (
                <div className="flex items-center gap-2 bg-green-500/20 px-3 py-1 rounded-full">
                  <Clock className="w-4 h-4 text-green-400" />
                  <span className="font-medium text-green-400">{movie.duration}</span>
                </div>
              )}
            </div>

            {/* Genres */}
            {movie.genres && movie.genres.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-6">
                {movie.genres.map((genre, index) => (
                  <span 
                    key={index}
                    className="px-3 py-1 bg-white/10 rounded-full text-sm text-gray-200 border border-white/10"
                  >
                    {genre}
                  </span>
                ))}
              </div>
            )}

            {/* Descripción Completa */}
            <div className="mb-8">
              <h3 className="text-xl font-bold text-white mb-3">Sinopsis</h3>
              <p className="text-gray-300 text-lg leading-relaxed">
                {movie.description || 'Una historia épica que no querrás perderte.'}
              </p>
            </div>

            {/* Botones de Acción */}
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => {
                  onPlayVideo(movie, progress);
                  onClose();
                }}
                className="bg-white text-black px-8 py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-3 hover:bg-gray-100 transition-all flex-1"
              >
                <Play className="w-6 h-6" />
                {hasProgress ? 'Continuar' : 'Reproducir Ahora'}
              </button>

              <button
                onClick={() => onToggleFavorite(movie.id)}
                className={`px-8 py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-3 transition-all flex-1 ${
                  isFavorite 
                    ? 'bg-red-500/20 text-red-400 border-2 border-red-500/50' 
                    : 'bg-gray-600/80 text-white border-2 border-gray-500/50'
                }`}
              >
                <Heart className={`w-6 h-6 ${isFavorite ? 'fill-current' : ''}`} />
                {isFavorite ? 'Quitar de Mi Lista' : 'Agregar a Mi Lista'}
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

const MyList = ({
  movies = [],
  onToggleFavorite = () => { },
  favorites = [],
  onPlayVideo = () => { },
  onGoToHome = () => { },
  watchProgress = {}
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGenre, setSelectedGenre] = useState('all');
  const [sortBy, setSortBy] = useState('added');
  const [viewMode, setViewMode] = useState('grid');
  const [selectedMovie, setSelectedMovie] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

    // Scroll to top on component mount
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  // Organizar películas por géneros
  const { genres, moviesByGenre } = useMemo(() => {
    const allGenres = new Set();
    const moviesByGenreMap = {};

    movies.forEach(movie => {
      const movieGenres = movie.genres && movie.genres.length > 0
        ? movie.genres
        : ['Sin Género'];

      movieGenres.forEach(genre => {
        allGenres.add(genre);
        if (!moviesByGenreMap[genre]) {
          moviesByGenreMap[genre] = [];
        }
        moviesByGenreMap[genre].push(movie);
      });
    });

    const sortedGenres = ['all', ...Array.from(allGenres).sort()];
    return { genres: sortedGenres, moviesByGenre: moviesByGenreMap };
  }, [movies]);

  // Filtrar y ordenar películas
  const filteredAndSortedMovies = useMemo(() => {
    let filtered = movies.filter(movie => {
      const matchesSearch = movie.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        movie.description?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesGenre = selectedGenre === 'all' ||
        (movie.genres && movie.genres.includes(selectedGenre)) ||
        (selectedGenre === 'Sin Género' && (!movie.genres || movie.genres.length === 0));

      return matchesSearch && matchesGenre;
    });

    switch (sortBy) {
      case 'title':
        filtered.sort((a, b) => a.title.localeCompare(b.title));
        break;
      case 'rating':
        filtered.sort((a, b) => parseFloat(b.rating) - parseFloat(a.rating));
        break;
      case 'year':
        filtered.sort((a, b) => parseInt(b.year) - parseInt(a.year));
        break;
      case 'added':
      default:
        break;
    }

    return filtered;
  }, [movies, searchTerm, selectedGenre, sortBy]);

  // Agrupar películas filtradas por género
  const filteredMoviesByGenre = useMemo(() => {
    const result = {};
    filteredAndSortedMovies.forEach(movie => {
      const movieGenres = movie.genres && movie.genres.length > 0
        ? movie.genres
        : ['Sin Género'];

      movieGenres.forEach(genre => {
        if (!result[genre]) {
          result[genre] = [];
        }
        result[genre].push(movie);
      });
    });
    return result;
  }, [filteredAndSortedMovies]);

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('');
    setSelectedGenre('all');
    setSortBy('added');
  };

  const openModal = (movie) => {
    setSelectedMovie(movie);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setTimeout(() => setSelectedMovie(null), 300);
  };

  if (movies.length === 0) {
    return (
      <motion.section
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="py-24 min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-black to-gray-900 relative z-40"
      >
        <div className="text-center max-w-lg mx-auto px-4">
          <motion.div
            className="w-32 h-32 bg-gradient-to-br from-red-500/20 to-pink-500/20 rounded-2xl flex items-center justify-center mx-auto mb-8 backdrop-blur-xl border border-red-500/20"
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 20 }}
          >
            <Heart className="w-16 h-16 text-red-400" />
          </motion.div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 drop-shadow-2xl">¡Tu Lista Está Vacía!</h2>
          <p className="text-gray-300 text-lg md:text-xl mb-8 leading-relaxed">
            Marca películas como favoritas en la página principal para verlas aquí. ¡Es hora de construir tu colección!
          </p>
          <motion.button
            onClick={onGoToHome}
            className="bg-gradient-to-r from-red-500 to-pink-500 text-white px-8 py-4 rounded-xl font-semibold flex items-center gap-3 mx-auto shadow-2xl hover:shadow-red-500/25 transition-all duration-300"
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95 }}
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="uppercase tracking-wide text-sm">Volver Atrás</span>
          </motion.button>
        </div>
      </motion.section>
    );
  }

  return (
    <motion.section
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
      className="py-8 min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 relative z-40"
    >
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-12 gap-6 mt-16">
          <div className="flex items-center gap-4">
            <motion.div
              className="w-12 h-12 bg-gradient-to-r from-red-500 to-pink-500 rounded-xl flex items-center justify-center shadow-lg"
              whileHover={{ rotate: 5, scale: 1.05 }}
            >
              <Heart className="w-6 h-6 text-white fill-current" />
            </motion.div>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-white drop-shadow-lg">Mi Lista</h2>
              <p className="text-gray-400 text-sm mt-2">
                {filteredAndSortedMovies.length} de {movies.length} {movies.length === 1 ? 'película' : 'películas'}
                {searchTerm && ` · Buscando: "${searchTerm}"`}
                {selectedGenre !== 'all' && ` · Género: ${selectedGenre}`}
              </p>
            </div>
          </div>

          {/* Controls */}
          <div className="flex flex-col sm:flex-row gap-4 w-full lg:w-auto">
            {/* Search */}
            <div className="relative flex-1 sm:flex-initial">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Buscar en mi lista..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-3 bg-gray-800/50 border border-gray-700/50 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-red-500/50 focus:bg-gray-800/80 transition-all duration-300 backdrop-blur-sm w-full sm:w-64"
              />
            </div>

            <div className="flex gap-3">
              {/* Genre Filter */}
              <div className="relative flex-1">
                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <select
                  value={selectedGenre}
                  onChange={(e) => setSelectedGenre(e.target.value)}
                  className="pl-10 pr-8 py-3 bg-gray-800/50 border border-gray-700/50 rounded-xl text-white focus:outline-none focus:border-red-500/50 focus:bg-gray-800/80 transition-all duration-300 backdrop-blur-sm w-full appearance-none"
                >
                  <option value="all">Todos los géneros</option>
                  {genres.filter(genre => genre !== 'all').map(genre => (
                    <option key={genre} value={genre}>{genre}</option>
                  ))}
                </select>
              </div>

              {/* Sort */}
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-4 py-3 bg-gray-800/50 border border-gray-700/50 rounded-xl text-white focus:outline-none focus:border-red-500/50 focus:bg-gray-800/80 transition-all duration-300 backdrop-blur-sm min-w-32"
              >
                <option value="added">Agregado</option>
                <option value="title">Título</option>
                <option value="rating">Rating</option>
                <option value="year">Año</option>
              </select>

              {/* View Mode Toggle */}
              <div className="flex bg-gray-800/50 rounded-xl border border-gray-700/50 overflow-hidden">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-3 transition-all duration-300 ${viewMode === 'grid' ? 'bg-red-500/20 text-red-400' : 'text-gray-400 hover:text-white'}`}
                >
                  <Grid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-3 transition-all duration-300 ${viewMode === 'list' ? 'bg-red-500/20 text-red-400' : 'text-gray-400 hover:text-white'}`}
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
            </div>

            <motion.button
              onClick={onGoToHome}
              className="group flex items-center justify-center gap-2 text-gray-300 hover:text-white bg-white/5 hover:bg-white/10 px-6 py-3 rounded-xl transition-all duration-300 backdrop-blur-sm border border-gray-700/50 hover:border-gray-600/50 whitespace-nowrap flex-shrink-0"
              whileHover={{ scale: 1.05, y: -1 }}
              whileTap={{ scale: 0.95 }}
            >
              <ArrowLeft className="w-4 h-4 group-hover:scale-110 transition-transform" />
              <span className="text-sm font-medium">Atrás</span>
            </motion.button>
          </div>
        </div>

        {/* Active Filters Bar */}
        {(searchTerm || selectedGenre !== 'all') && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-4 mb-8 p-4 bg-gray-800/30 rounded-xl backdrop-blur-sm border border-gray-700/50"
          >
            <span className="text-gray-400 text-sm">Filtros activos:</span>
            <div className="flex flex-wrap gap-2">
              {searchTerm && (
                <motion.span
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="inline-flex items-center gap-2 bg-red-500/20 text-red-400 px-3 py-1 rounded-full text-sm"
                >
                  Buscar: "{searchTerm}"
                  <button onClick={() => setSearchTerm('')}>
                    <X className="w-3 h-3 hover:text-red-300" />
                  </button>
                </motion.span>
              )}
              {selectedGenre !== 'all' && (
                <motion.span
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="inline-flex items-center gap-2 bg-blue-500/20 text-blue-400 px-3 py-1 rounded-full text-sm"
                >
                  Género: {selectedGenre}
                  <button onClick={() => setSelectedGenre('all')}>
                    <X className="w-3 h-3 hover:text-blue-300" />
                  </button>
                </motion.span>
              )}
            </div>
            <motion.button
              onClick={clearFilters}
              className="ml-auto text-gray-400 hover:text-white text-sm flex items-center gap-2 transition-colors"
              whileHover={{ scale: 1.05 }}
            >
              <Trash2 className="w-4 h-4" />
              Limpiar todo
            </motion.button>
          </motion.div>
        )}

        {/* Content */}
        {viewMode === 'grid' ? (
          /* Grid View */
          <div className="space-y-16">
            {Object.entries(filteredMoviesByGenre).map(([genre, genreMovies]) => (
              <motion.div
                key={genre}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div className="flex items-center gap-4 mb-8">
                  <div className="w-1 h-8 bg-gradient-to-b from-red-500 to-pink-500 rounded-full"></div>
                  <h3 className="text-2xl md:text-3xl font-bold text-white">{genre}</h3>
                  <span className="text-gray-400 text-lg">({genreMovies.length})</span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  <AnimatePresence>
                    {genreMovies.map((movie) => (
                      <MovieCard
                        key={movie.id}
                        movie={movie}
                        onToggleFavorite={onToggleFavorite}
                        onPlayVideo={onPlayVideo}
                        favorites={favorites}
                        viewMode={viewMode}
                        watchProgress={watchProgress}
                        onOpenModal={openModal}
                      />
                    ))}
                  </AnimatePresence>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          /* List View */
          <div className="space-y-4">
            <AnimatePresence>
              {filteredAndSortedMovies.map((movie) => (
                <MovieCard
                  key={movie.id}
                  movie={movie}
                  onToggleFavorite={onToggleFavorite}
                  onPlayVideo={onPlayVideo}
                  favorites={favorites}
                  viewMode={viewMode}
                  watchProgress={watchProgress}
                  onOpenModal={openModal}
                />
              ))}
            </AnimatePresence>
          </div>
        )}

        {/* No Results */}
        {filteredAndSortedMovies.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-20"
          >
            <div className="w-24 h-24 bg-gray-800/50 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Search className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-3">No se encontraron películas</h3>
            <p className="text-gray-400 max-w-md mx-auto mb-6">
              No hay películas que coincidan con tu búsqueda "{searchTerm}"
              {selectedGenre !== 'all' && ` en el género ${selectedGenre}`}.
            </p>
            <motion.button
              onClick={clearFilters}
              className="bg-gray-700/50 hover:bg-gray-600/50 text-white px-6 py-3 rounded-lg transition-all duration-300 border border-gray-600/50 hover:border-gray-500/50"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Limpiar filtros
            </motion.button>
          </motion.div>
        )}
      </div>

      {/* Modal de Detalles */}
      {selectedMovie && (
        <MovieDetailModal
          movie={selectedMovie}
          isOpen={isModalOpen}
          onClose={closeModal}
          onPlayVideo={onPlayVideo}
          onToggleFavorite={onToggleFavorite}
          isFavorite={favorites.includes(selectedMovie.id)}
          progress={watchProgress[selectedMovie.id] || getWatchProgress(selectedMovie.id) || 0}
        />
      )}
    </motion.section>
  );
};

// Improved MovieCard component with modal support
const MovieCard = ({ movie, onToggleFavorite, onPlayVideo, favorites, viewMode = 'grid', watchProgress = {}, onOpenModal }) => {
  const [isHovered, setIsHovered] = useState(false);
  const progress = watchProgress[movie.id] || getWatchProgress(movie.id) || 0;
  const hasProgress = progress > 0;

  if (viewMode === 'list') {
    return (
      <motion.div
        layout
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: 20 }}
        transition={{ duration: 0.3 }}
        className="group bg-gray-800/30 backdrop-blur-sm rounded-2xl border border-gray-700/50 hover:border-gray-600/70 transition-all duration-500 hover:shadow-2xl hover:shadow-red-500/10 overflow-hidden"
        onHoverStart={() => setIsHovered(true)}
        onHoverEnd={() => setIsHovered(false)}
      >
        <div className="flex">
          {/* Image */}
          <div className="relative w-32 h-48 flex-shrink-0">
            <img
              src={movie.image}
              alt={movie.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-gray-900/50 to-transparent" />

            {/* Progress Bar */}
            {hasProgress && (
              <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/80 to-transparent">
                <div className="flex items-center gap-2 mb-1">
                  <RotateCcw className="w-3 h-3 text-green-400" />
                  <span className="text-green-400 text-xs font-semibold">{progress}%</span>
                </div>
                <div className="w-full bg-gray-700/50 rounded-full h-1">
                  <div
                    className="bg-green-500 h-1 rounded-full transition-all duration-500"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            )}

            {/* Rating */}
            <div className="absolute top-2 left-2 flex items-center gap-1 bg-black/80 backdrop-blur-sm px-2 py-1 rounded-full">
              <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
              <span className="text-white text-xs font-semibold">{movie.rating}</span>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 p-6">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="text-white font-bold text-xl mb-2 group-hover:text-red-400 transition-colors">
                  {movie.title}
                </h3>
                <div className="flex items-center gap-4 text-gray-400 text-sm mb-3">
                  {movie.duration && (
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      <span>{movie.duration}</span>
                    </div>
                  )}
                  {movie.year && (
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      <span>{movie.year}</span>
                    </div>
                  )}
                </div>
              </div>

              <motion.button
                onClick={(e) => { e.stopPropagation(); onToggleFavorite(movie.id); }}
                className="p-2 bg-black/50 hover:bg-red-500 rounded-lg backdrop-blur-sm transition-all duration-300"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <X className="w-4 h-4 text-white" />
              </motion.button>
            </div>

            {/* Description - Truncada con Ver más */}
            <div className="mb-4">
              <p className="text-gray-300 text-sm line-clamp-2 leading-relaxed">
                {movie.description}
              </p>
              <button
                onClick={(e) => { e.stopPropagation(); onOpenModal(movie); }}
                className="mt-1 text-white text-xs font-semibold hover:text-gray-300 transition-colors flex items-center gap-1"
              >
                <span>Ver más</span>
                <Info className="w-3 h-3" />
              </button>
            </div>

            <div className="flex items-center gap-3">
              <motion.button
                onClick={(e) => { e.stopPropagation(); onPlayVideo(movie, progress); }}
                className="bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 text-white px-6 py-2.5 rounded-lg text-sm font-semibold transition-all duration-300 flex items-center gap-2"
                whileHover={{ scale: 1.05, y: -1 }}
                whileTap={{ scale: 0.95 }}
              >
                <Play className="w-4 h-4" />
                <span>{hasProgress ? 'Continuar' : 'Reproducir'}</span>
              </motion.button>

              {/* Progress Info */}
              {hasProgress && (
                <div className="flex items-center gap-2 text-green-400 text-xs">
                  <RotateCcw className="w-3 h-3" />
                  <span>{progress}% visto</span>
                </div>
              )}

              {/* Genres */}
              <div className="flex gap-2">
                {movie.genres?.slice(0, 2).map((genre, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-gray-700/50 text-gray-300 text-xs rounded-full border border-gray-600/50"
                  >
                    {genre}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  // Grid View con progreso
  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{ duration: 0.3 }}
      className="group relative bg-gray-800/30 backdrop-blur-sm rounded-2xl overflow-hidden border border-gray-700/50 hover:border-gray-600/70 transition-all duration-500 hover:shadow-2xl hover:shadow-red-500/10"
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
    >
      {/* Image Container con progreso */}
      <div className="relative h-64 overflow-hidden">
        <img
          src={movie.image}
          alt={movie.title}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-gray-900/90 via-transparent to-transparent" />

        {/* Progress Bar */}
        {hasProgress && (
          <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/80 to-transparent z-20">
            <div className="flex items-center gap-2 mb-1">
              <RotateCcw className="w-3 h-3 text-green-400" />
              <span className="text-green-400 text-xs font-semibold">{progress}%</span>
            </div>
            <div className="w-full bg-gray-700/50 rounded-full h-1.5">
              <div
                className="bg-green-500 h-1.5 rounded-full transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}

        <div className="absolute top-3 right-3 flex gap-2 z-20">
          <motion.button
            onClick={(e) => { e.stopPropagation(); onToggleFavorite(movie.id); }}
            className="p-2 bg-black/80 hover:bg-red-500 rounded-lg backdrop-blur-sm transition-all duration-300 shadow-lg"
            whileHover={{ scale: 1.1, rotate: 5 }}
            whileTap={{ scale: 0.9 }}
          >
            <X className="w-4 h-4 text-white" />
          </motion.button>
        </div>

        <AnimatePresence>
          {isHovered && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm z-10"
              style={{ pointerEvents: 'none' }}
            >
              <motion.button
                onClick={(e) => { e.stopPropagation(); onPlayVideo(movie, progress); }}
                className="bg-gradient-to-r from-red-500 to-pink-500 text-white p-4 rounded-full shadow-2xl flex items-center gap-2"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                style={{ pointerEvents: 'auto' }}
              >
                <Play className="w-5 h-5" />
                <span>{hasProgress ? 'Continuar' : 'Reproducir'}</span>
              </motion.button>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="absolute bottom-3 left-3 flex items-center gap-1 bg-black/80 backdrop-blur-sm px-2 py-1 rounded-full z-20">
          <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
          <span className="text-white text-sm font-semibold">{movie.rating}</span>
        </div>
      </div>

      {/* Content */}
      <div className="p-5">
        <h3 className="text-white font-bold text-lg mb-2 line-clamp-1 group-hover:text-red-400 transition-colors">
          {movie.title}
        </h3>

        <div className="flex items-center gap-3 text-gray-400 text-sm mb-3">
          {movie.duration && (
            <div className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              <span>{movie.duration}</span>
            </div>
          )}
          {movie.year && (
            <div className="flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              <span>{movie.year}</span>
            </div>
          )}
        </div>

        {/* Description - Truncada con Ver más */}
        <div className="mb-4">
          <p className="text-gray-300 text-sm line-clamp-2 leading-relaxed">
            {movie.description}
          </p>
          <button
            onClick={(e) => { e.stopPropagation(); onOpenModal(movie); }}
            className="mt-1 text-white text-xs font-semibold hover:text-gray-300 transition-colors flex items-center gap-1"
          >
            <span>Ver más</span>
            <Info className="w-3 h-3" />
          </button>
        </div>

        <div className="flex gap-2">
          <motion.button
            onClick={(e) => { e.stopPropagation(); onPlayVideo(movie, progress); }}
            className="flex-1 bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 text-white py-2.5 rounded-lg text-sm font-semibold transition-all duration-300 flex items-center justify-center gap-2"
            whileHover={{ scale: 1.02, y: -1 }}
            whileTap={{ scale: 0.98 }}
          >
            <Play className="w-3 h-3" />
            <span>{hasProgress ? 'Continuar' : 'Reproducir'}</span>
          </motion.button>

          <AnimatePresence>
            {isHovered && (
              <motion.button
                onClick={(e) => { e.stopPropagation(); onToggleFavorite(movie.id); }}
                className="px-4 bg-gray-700/80 hover:bg-red-500/80 text-gray-300 hover:text-white rounded-lg transition-all duration-300 border border-gray-600/50 hover:border-red-500/50 flex items-center gap-2"
                initial={{ opacity: 0, width: 0 }}
                animate={{ opacity: 1, width: 'auto' }}
                exit={{ opacity: 0, width: 0 }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <X className="w-3 h-3" />
                <span className="whitespace-nowrap text-xs">Quitar</span>
              </motion.button>
            )}
          </AnimatePresence>
        </div>

        {/* Progress Info */}
        {hasProgress && (
          <div className="flex items-center gap-2 text-green-400 text-xs mt-2">
            <RotateCcw className="w-3 h-3" />
            <span>{progress}% visto</span>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default MyList;